import React from "react";

type FormatState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  unordered: boolean;
  ordered: boolean;
  link: boolean;
  color: string;
};

const DEFAULT_TEXT_COLOR = "#000000";

const EMPTY_FORMAT_STATE: FormatState = {
  bold: false,
  italic: false,
  underline: false,
  unordered: false,
  ordered: false,
  link: false,
  color: DEFAULT_TEXT_COLOR,
};

const CAN_USE_DOM = typeof window !== "undefined" && typeof window.document !== "undefined";
const ALLOWED_TAGS = new Set([
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "UL",
  "OL",
  "LI",
  "A",
  "P",
  "BR",
  "SPAN",
  "FONT",
]);

const ALLOWED_LINK_ATTRIBUTES = new Set(["href", "target", "rel"]);

const SAFE_COLOR_PATTERN =
  /^(#[0-9a-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|hsla?\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|[a-z]+)$/i;

const escapeHtml = (input: string): string => {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const ensureSafeUrl = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (/^mailto:/i.test(trimmed)) {
    return trimmed;
  }
  return null;
};

const isSafeColor = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed || /url\s*\(|expression\s*\(|javascript:/i.test(trimmed)) {
    return false;
  }
  return SAFE_COLOR_PATTERN.test(trimmed);
};

const extractStyleProperty = (style: string, property: string): string | null => {
  const match = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, "i").exec(style);
  if (!match) {
    return null;
  }
  return match[1].trim();
};

const extractColorFromStyle = (style: string): string | null => {
  const color = extractStyleProperty(style, "color");
  if (!color) {
    return null;
  }
  return isSafeColor(color) ? color : null;
};

const isBoldFontWeight = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "bold" || normalized === "bolder") {
    return true;
  }
  const numeric = Number(normalized);
  return Number.isFinite(numeric) && numeric >= 600;
};

const isItalicFontStyle = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return normalized === "italic" || normalized === "oblique";
};

const hasUnderlineDecoration = (value: string): boolean => {
  return /underline/i.test(value);
};

const isSafeIndentValue = (value: string): boolean => {
  return /^-?\d+(\.\d+)?(px|pt|em|rem|%)$/i.test(value.trim()) && !/^0(?:\.0+)?(?:px|pt|em|rem|%)?$/i.test(value.trim());
};

const wrapNodeWithTag = (doc: Document, node: Node, tagName: string): HTMLElement => {
  const wrapper = doc.createElement(tagName);
  wrapper.appendChild(node);
  return wrapper;
};

const rgbToHex = (value: string): string => {
  const trimmed = value.trim();
  if (/^#[0-9a-f]{3,8}$/i.test(trimmed)) {
    if (trimmed.length === 4) {
      const r = trimmed[1];
      const g = trimmed[2];
      const b = trimmed[3];
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return trimmed.slice(0, 7).toLowerCase();
  }
  const match = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(trimmed);
  if (!match) {
    return DEFAULT_TEXT_COLOR;
  }
  const toHex = (part: string) => {
    const n = Math.max(0, Math.min(255, Number(part)));
    return n.toString(16).padStart(2, "0");
  };
  return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
};

const sanitizeLinkElement = (element: HTMLAnchorElement) => {
  const href = element.getAttribute("href") ?? "";
  const safeHref = ensureSafeUrl(href);
  if (!safeHref) {
    const parent = element.parentNode;
    while (element.firstChild) {
      parent?.insertBefore(element.firstChild, element);
    }
    parent?.removeChild(element);
    return;
  }
  element.setAttribute("href", safeHref);
  element.setAttribute("target", "_blank");
  element.setAttribute("rel", "noopener noreferrer");
  Array.from(element.attributes).forEach((attr) => {
    if (!ALLOWED_LINK_ATTRIBUTES.has(attr.name)) {
      element.removeAttribute(attr.name);
    }
  });
};

const unwrapElement = (el: HTMLElement) => {
  const parent = el.parentNode;
  if (!parent) {
    return;
  }
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
};

const sanitizeHtml = (input: string): string => {
  if (!input) {
    return "";
  }
  if (!CAN_USE_DOM) {
    return input;
  }
  const doc = window.document.implementation.createHTMLDocument("");
  if (!doc.body) {
    return input;
  }
  doc.body.innerHTML = input;

  const visit = (node: Node | null) => {
    if (!node) {
      return;
    }
    if (node.nodeType === Node.COMMENT_NODE) {
      node.parentNode?.removeChild(node);
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === "SPAN") {
        const style = el.getAttribute("style") ?? "";
        const color = extractColorFromStyle(style);
        const fontWeight = extractStyleProperty(style, "font-weight");
        const fontStyle = extractStyleProperty(style, "font-style");
        const textDecoration =
          extractStyleProperty(style, "text-decoration-line") ??
          extractStyleProperty(style, "text-decoration");
        const needsBold = fontWeight ? isBoldFontWeight(fontWeight) : false;
        const needsItalic = fontStyle ? isItalicFontStyle(fontStyle) : false;
        const needsUnderline = textDecoration ? hasUnderlineDecoration(textDecoration) : false;

        if (needsBold || needsItalic || needsUnderline) {
          const fragment = doc.createDocumentFragment();
          while (el.firstChild) {
            fragment.appendChild(el.firstChild);
          }

          let content: Node = fragment;
          if (needsUnderline) {
            content = wrapNodeWithTag(doc, content, "u");
          }
          if (needsItalic) {
            content = wrapNodeWithTag(doc, content, "i");
          }
          if (needsBold) {
            content = wrapNodeWithTag(doc, content, "b");
          }
          if (color) {
            const colored = doc.createElement("span");
            colored.setAttribute("style", `color: ${color}`);
            colored.appendChild(content);
            content = colored;
          }

          el.parentNode?.replaceChild(content, el);
          visit(content);
          return;
        }

        Array.from(el.attributes).forEach((attr) => {
          el.removeAttribute(attr.name);
        });
        if (color) {
          el.setAttribute("style", `color: ${color}`);
        } else {
          const children = Array.from(el.childNodes);
          unwrapElement(el);
          children.forEach(visit);
          return;
        }
      } else if (el.tagName === "FONT") {
        const colorAttr = el.getAttribute("color") ?? "";
        const color = isSafeColor(colorAttr) ? colorAttr.trim() : null;
        Array.from(el.attributes).forEach((attr) => {
          el.removeAttribute(attr.name);
        });
        if (color) {
          el.setAttribute("color", color);
        } else {
          const children = Array.from(el.childNodes);
          unwrapElement(el);
          children.forEach(visit);
          return;
        }
      } else if (ALLOWED_TAGS.has(el.tagName)) {
        if (el.tagName === "A") {
          sanitizeLinkElement(el as HTMLAnchorElement);
        } else if (el.tagName === "P") {
          const style = el.getAttribute("style") ?? "";
          const indent =
            extractStyleProperty(style, "margin-left") ?? extractStyleProperty(style, "padding-left");
          Array.from(el.attributes).forEach((attr) => {
            el.removeAttribute(attr.name);
          });
          if (indent && isSafeIndentValue(indent)) {
            el.setAttribute("style", `margin-left: ${indent}`);
          }
        } else {
          Array.from(el.attributes).forEach((attr) => {
            el.removeAttribute(attr.name);
          });
        }
      } else if (el.tagName === "DIV") {
        const replacement = doc.createElement("p");
        while (el.firstChild) {
          replacement.appendChild(el.firstChild);
        }
        el.parentNode?.replaceChild(replacement, el);
        visit(replacement);
        return;
      } else if (/^H[1-6]$/.test(el.tagName)) {
        const replacement = doc.createElement("p");
        const bold = doc.createElement("b");
        while (el.firstChild) {
          bold.appendChild(el.firstChild);
        }
        replacement.appendChild(bold);
        el.parentNode?.replaceChild(replacement, el);
        visit(replacement);
        return;
      } else {
        const children = Array.from(el.childNodes);
        unwrapElement(el);
        children.forEach(visit);
        return;
      }
    }
    Array.from(node.childNodes).forEach(visit);
  };

  visit(doc.body);
  return doc.body ? doc.body.innerHTML : input;
};

const isVisuallyEmptyBlock = (el: HTMLElement): boolean => {
  const plain = (el.textContent ?? "")
    .replace(/\u200B/g, "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, "");
  return plain.length === 0;
};

const stripEdgePasteNoise = (container: HTMLElement, edge: "start" | "end") => {
  const fromStart = edge === "start";
  while (true) {
    const node = fromStart ? container.firstChild : container.lastChild;
    if (!node) {
      break;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      const trimmed = fromStart
        ? text.replace(/[\u200B\u00a0]+/g, " ").replace(/^\s+/g, "")
        : text.replace(/[\u200B\u00a0]+/g, " ").replace(/\s+$/g, "");
      if (!trimmed) {
        container.removeChild(node);
        continue;
      }
      if (trimmed !== text) {
        node.textContent = trimmed;
      }
      break;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      container.removeChild(node);
      continue;
    }
    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      container.removeChild(el);
      continue;
    }
    if (el.tagName === "P" || el.tagName === "DIV") {
      stripEdgePasteNoise(el, edge);
      if (isVisuallyEmptyBlock(el)) {
        container.removeChild(el);
        continue;
      }
    }
    break;
  }
};

// Strip clipboard wrappers and leading/trailing empty blocks that render as blank lines.
const normalizePastedHtml = (html: string): string => {
  if (!html || !CAN_USE_DOM) {
    return html;
  }
  const doc = window.document.implementation.createHTMLDocument("");
  if (!doc.body) {
    return html;
  }
  doc.body.innerHTML = html;

  Array.from(doc.body.querySelectorAll("p")).forEach((paragraph) => {
    if (isVisuallyEmptyBlock(paragraph)) {
      paragraph.remove();
    }
  });

  const significantChildren = Array.from(doc.body.childNodes).filter((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return !!(node.textContent ?? "").trim();
    }
    return node.nodeType === Node.ELEMENT_NODE;
  });

  if (
    significantChildren.length === 1 &&
    significantChildren[0] instanceof HTMLElement &&
    significantChildren[0].tagName === "P"
  ) {
    const paragraph = significantChildren[0];
    while (paragraph.firstChild) {
      doc.body.insertBefore(paragraph.firstChild, paragraph);
    }
    paragraph.remove();
  }

  stripEdgePasteNoise(doc.body, "start");
  stripEdgePasteNoise(doc.body, "end");
  return doc.body.innerHTML;
};

const isNodeWithin = (node: Node | null, container: HTMLElement | null): boolean => {
  if (!node || !container) {
    return false;
  }
  let current: Node | null = node;
  while (current) {
    if (current === container) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
};

const findAncestorWithTag = (
  node: Node | null,
  container: HTMLElement,
  tags: string[]
): HTMLElement | null => {
  let current: Node | null = node;
  if (current?.nodeType === Node.TEXT_NODE) {
    current = current.parentElement;
  }
  while (current && current !== container) {
    if (current instanceof HTMLElement && tags.includes(current.tagName)) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
};

const INLINE_FORMAT_TAG_GROUPS: Record<"bold" | "italic" | "underline", string[]> = {
  bold: ["B", "STRONG"],
  italic: ["I", "EM"],
  underline: ["U"],
};

const isInlineFormatActive = (
  container: HTMLElement,
  format: "bold" | "italic" | "underline"
): boolean => {
  if (!CAN_USE_DOM) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const anchorNode = selection.anchorNode;
  if (!isNodeWithin(anchorNode, container)) {
    return false;
  }
  return findAncestorWithTag(anchorNode, container, INLINE_FORMAT_TAG_GROUPS[format]) !== null;
};

const toggleInlineFormat = (tagName: "B" | "I" | "U", container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  const formatKey = tagName === "B" ? "bold" : tagName === "I" ? "italic" : "underline";
  const existing = findAncestorWithTag(selection.anchorNode, container, INLINE_FORMAT_TAG_GROUPS[formatKey]);
  if (existing) {
    unwrapElement(existing);
    return true;
  }

  const el = window.document.createElement(tagName);
  if (selection.isCollapsed) {
    const marker = window.document.createTextNode("\u200B");
    el.appendChild(marker);
    range.insertNode(el);
    const caretRange = window.document.createRange();
    caretRange.setStart(marker, 1);
    caretRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caretRange);
    return true;
  }

  const contents = range.extractContents();
  el.appendChild(contents);
  range.insertNode(el);
  const selectedRange = window.document.createRange();
  selectedRange.selectNodeContents(el);
  selection.removeAllRanges();
  selection.addRange(selectedRange);
  return true;
};

const isListActive = (container: HTMLElement, listTag: "UL" | "OL"): boolean => {
  if (!CAN_USE_DOM) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const anchorNode = selection.anchorNode;
  if (!isNodeWithin(anchorNode, container)) {
    return false;
  }
  return findAncestorWithTag(anchorNode, container, [listTag]) !== null;
};

const unwrapList = (list: HTMLElement) => {
  const parent = list.parentNode;
  if (!parent) {
    return;
  }
  const fragment = window.document.createDocumentFragment();
  Array.from(list.children).forEach((item, index) => {
    while (item.firstChild) {
      fragment.appendChild(item.firstChild);
    }
    if (index < list.children.length - 1) {
      fragment.appendChild(window.document.createElement("br"));
    }
  });
  parent.replaceChild(fragment, list);
};

const toggleList = (listTag: "UL" | "OL", container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  const existingList = findAncestorWithTag(selection.anchorNode, container, ["UL", "OL"]);
  if (existingList) {
    if (existingList.tagName === listTag) {
      unwrapList(existingList);
      return true;
    }
    const replacement = window.document.createElement(listTag);
    while (existingList.firstChild) {
      replacement.appendChild(existingList.firstChild);
    }
    existingList.parentNode?.replaceChild(replacement, existingList);
    return true;
  }

  const list = window.document.createElement(listTag);
  const listItem = window.document.createElement("li");
  if (selection.isCollapsed) {
    listItem.appendChild(window.document.createTextNode("\u200B"));
    list.appendChild(listItem);
    range.insertNode(list);
    const caretRange = window.document.createRange();
    caretRange.setStart(listItem.firstChild!, 1);
    caretRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caretRange);
    return true;
  }

  const contents = range.extractContents();
  listItem.appendChild(contents);
  list.appendChild(listItem);
  range.insertNode(list);
  const selectedRange = window.document.createRange();
  selectedRange.selectNodeContents(listItem);
  selection.removeAllRanges();
  selection.addRange(selectedRange);
  return true;
};

const insertHtmlAtCaret = (html: string, container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container || !html) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  range.deleteContents();
  const temp = window.document.createElement("div");
  temp.innerHTML = html;
  const insertedNodes: Node[] = [];
  const fragment = window.document.createDocumentFragment();
  while (temp.firstChild) {
    const node = temp.firstChild;
    insertedNodes.push(node);
    fragment.appendChild(node);
  }
  range.insertNode(fragment);

  if (insertedNodes.length > 0) {
    const lastNode = insertedNodes[insertedNodes.length - 1];
    range.setStartAfter(lastNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
  return true;
};

const insertParagraph = (container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  range.deleteContents();
  const paragraph = window.document.createElement("p");
  paragraph.appendChild(window.document.createElement("br"));
  range.insertNode(paragraph);
  const caretRange = window.document.createRange();
  caretRange.setStart(paragraph, 0);
  caretRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(caretRange);
  return true;
};

const removeLinkAtSelection = (container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const existing = findAncestorWithTag(selection.anchorNode, container, ["A"]);
  if (existing) {
    unwrapElement(existing);
    return true;
  }
  return false;
};

const createLinkAtSelection = (href: string, container: HTMLElement | null): boolean => {
  const safe = ensureSafeUrl(href);
  if (!CAN_USE_DOM || !container || !safe) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  const existing = findAncestorWithTag(selection.anchorNode, container, ["A"]);
  if (existing instanceof HTMLAnchorElement) {
    existing.setAttribute("href", safe);
    existing.setAttribute("target", "_blank");
    existing.setAttribute("rel", "noopener noreferrer");
    return true;
  }

  const anchor = window.document.createElement("a");
  anchor.setAttribute("href", safe);
  anchor.setAttribute("target", "_blank");
  anchor.setAttribute("rel", "noopener noreferrer");

  if (selection.isCollapsed) {
    anchor.textContent = safe;
    range.insertNode(anchor);
    const caretRange = window.document.createRange();
    caretRange.setStartAfter(anchor);
    caretRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caretRange);
    return true;
  }

  const contents = range.extractContents();
  anchor.appendChild(contents);
  range.insertNode(anchor);
  const selectedRange = window.document.createRange();
  selectedRange.selectNodeContents(anchor);
  selection.removeAllRanges();
  selection.addRange(selectedRange);
  return true;
};

const findCurrentLinkHref = (container: HTMLElement | null): string | undefined => {
  if (!CAN_USE_DOM) {
    return undefined;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return undefined;
  }
  const anchor = selection.anchorNode;
  if (!anchor) {
    return undefined;
  }
  let current: Node | null = anchor;
  while (current && current !== container) {
    if (current instanceof HTMLAnchorElement) {
      return current.getAttribute("href") ?? undefined;
    }
    current = current.parentNode;
  }
  const focus = selection.focusNode;
  current = focus;
  while (current && current !== container) {
    if (current instanceof HTMLAnchorElement) {
      return current.getAttribute("href") ?? undefined;
    }
    current = current.parentNode;
  }
  return undefined;
};

const findCurrentTextColor = (container: HTMLElement | null): string => {
  if (!CAN_USE_DOM || !container) {
    return DEFAULT_TEXT_COLOR;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return DEFAULT_TEXT_COLOR;
  }
  let node: Node | null = selection.anchorNode;
  if (!isNodeWithin(node, container)) {
    return DEFAULT_TEXT_COLOR;
  }
  if (node && node.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }
  let current = node as HTMLElement | null;
  while (current && current !== container) {
    if (current.tagName === "SPAN") {
      const color = extractColorFromStyle(current.getAttribute("style") ?? "");
      if (color) {
        return rgbToHex(color);
      }
    }
    if (current.tagName === "FONT") {
      const color = current.getAttribute("color");
      if (color && isSafeColor(color)) {
        return rgbToHex(color);
      }
    }
    current = current.parentElement;
  }
  return DEFAULT_TEXT_COLOR;
};

const applyTextColor = (color: string, container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container || !isSafeColor(color)) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  const span = window.document.createElement("span");
  span.setAttribute("style", `color: ${color}`);

  if (selection.isCollapsed) {
    const marker = window.document.createTextNode("\u200B");
    span.appendChild(marker);
    range.insertNode(span);
    const caretRange = window.document.createRange();
    caretRange.setStart(marker, 1);
    caretRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caretRange);
    return true;
  }

  const contents = range.extractContents();
  span.appendChild(contents);
  range.insertNode(span);
  const selectedRange = window.document.createRange();
  selectedRange.selectNodeContents(span);
  selection.removeAllRanges();
  selection.addRange(selectedRange);
  return true;
};

const refreshSelectionState = (
  editor: HTMLElement | null,
  setState: React.Dispatch<React.SetStateAction<FormatState>>
) => {
  if (!CAN_USE_DOM || !editor) {
    setState(EMPTY_FORMAT_STATE);
    return;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    setState(EMPTY_FORMAT_STATE);
    return;
  }
  const anchorNode = selection.anchorNode;
  if (!isNodeWithin(anchorNode, editor)) {
    setState(EMPTY_FORMAT_STATE);
    return;
  }
  setState({
    bold: isInlineFormatActive(editor, "bold"),
    italic: isInlineFormatActive(editor, "italic"),
    underline: isInlineFormatActive(editor, "underline"),
    unordered: isListActive(editor, "UL"),
    ordered: isListActive(editor, "OL"),
    link: findCurrentLinkHref(editor) !== undefined,
    color: findCurrentTextColor(editor),
  });
};

const focusEditor = (editor: HTMLElement | null) => {
  if (!editor || !CAN_USE_DOM) {
    return;
  }
  editor.focus();
};

const removeLeadingTabAtCaret = (): boolean => {
  if (!CAN_USE_DOM) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return false;
  }
  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) {
    return false;
  }
  const text = node.textContent ?? "";
  const offset = range.startOffset;
  if (offset <= 0 || text[offset - 1] !== "\t") {
    return false;
  }
  const deleteRange = range.cloneRange();
  deleteRange.setStart(node, offset - 1);
  deleteRange.setEnd(node, offset);
  deleteRange.deleteContents();
  return true;
};

const insertTextAtCaret = (text: string, container: HTMLElement | null): boolean => {
  if (!CAN_USE_DOM || !container || !text) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!isNodeWithin(range.commonAncestorContainer, container)) {
    return false;
  }

  range.deleteContents();
  const textNode = window.document.createTextNode(text);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

export const extractPlainText = (html?: string | null): string => {
  if (!html) {
    return "";
  }
  if (!CAN_USE_DOM) {
    return html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ");
  }
  const temp = window.document.createElement("div");
  temp.innerHTML = html;
  return (temp.innerText || temp.textContent || "").replace(/\u00a0/g, " ");
};

export const getRichTextPlainLength = (html?: string | null): number => {
  return extractPlainText(html).length;
};

export const cleanRichTextValue = (html?: string | null): string | null => {
  const sanitized = sanitizeHtml(html ?? "");
  const plain = extractPlainText(sanitized).replace(/\s+/g, "");
  return plain.length > 0 ? sanitized : null;
};

export interface RichTextEditorProps {
  value?: string | null;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  maxHeight?: number;
  "aria-label"?: string;
}

const buttonBaseStyle: React.CSSProperties = {
  border: "none",
  background: "none",
  padding: "4px 6px",
  fontSize: 14,
  cursor: "pointer",
  borderRadius: 4,
  lineHeight: 1,
};

const toolbarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 4,
  borderBottom: "1px solid #f0f0f0",
  padding: "6px 8px",
  background: "#fafafa",
};

const placeholderStyle: React.CSSProperties = {
  position: "absolute",
  top: 8,
  left: 12,
  right: 12,
  color: "#bfbfbf",
  pointerEvents: "none",
};

const colorInputStyle: React.CSSProperties = {
  width: 28,
  height: 24,
  padding: 0,
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  background: "transparent",
  cursor: "pointer",
};

const RichTextEditor = React.forwardRef<HTMLDivElement, RichTextEditorProps>(
  ({ value, onChange, placeholder, disabled, className, id, maxHeight = 280, "aria-label": ariaLabel }, ref) => {
    const editorRef = React.useRef<HTMLDivElement | null>(null);
    const lastValueRef = React.useRef<string>("");
    const [isFocused, setIsFocused] = React.useState(false);
    const [formatState, setFormatState] = React.useState<FormatState>(EMPTY_FORMAT_STATE);

    React.useImperativeHandle(ref, () => editorRef.current as HTMLDivElement);

    React.useEffect(() => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      const sanitized = sanitizeHtml(value ?? "");
      if (sanitized === lastValueRef.current) {
        return;
      }
      lastValueRef.current = sanitized;
      if (editor.innerHTML !== sanitized) {
        editor.innerHTML = sanitized;
      }
    }, [value]);

    const emitChange = React.useCallback(() => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      try {
        const raw = editor.innerHTML;
        const sanitized = sanitizeHtml(raw);
        if (sanitized !== raw) {
          if (editor && editor.innerHTML !== sanitized) {
            editor.innerHTML = sanitized;
          }
        }
        if (sanitized !== lastValueRef.current) {
          lastValueRef.current = sanitized;
          onChange?.(sanitized);
        }
      } catch (error) {
        console.warn("Error sanitizing rich text:", error);
        try {
          const raw = editor?.innerHTML ?? "";
          if (raw !== lastValueRef.current) {
            lastValueRef.current = raw;
            onChange?.(raw);
          }
        } catch (fallbackError) {
          console.error("Error in fallback:", fallbackError);
        }
      }
    }, [onChange]);

    const handleInput = React.useCallback(() => {
      emitChange();
      refreshSelectionState(editorRef.current, setFormatState);
    }, [emitChange]);

    const handleFocus = React.useCallback(() => {
      setIsFocused(true);
      refreshSelectionState(editorRef.current, setFormatState);
    }, []);

    const handleBlur = React.useCallback(() => {
      setIsFocused(false);
      emitChange();
      refreshSelectionState(editorRef.current, setFormatState);
    }, [emitChange]);

    const handlePaste = React.useCallback(
      (event: React.ClipboardEvent<HTMLDivElement>) => {
        if (!CAN_USE_DOM) {
          return;
        }
        event.preventDefault();

        const clipboardHtml = event.clipboardData.getData("text/html");
        const text = event.clipboardData.getData("text/plain");

        let html = clipboardHtml ? normalizePastedHtml(sanitizeHtml(clipboardHtml)) : "";
        if (!html && text) {
          html = text
            .replace(/\r\n/g, "\n")
            .replace(/^\n+|\n+$/g, "")
            .split("\n")
            .map((line) => escapeHtml(line))
            .join("<br>");
        }
        if (!html) {
          return;
        }

        focusEditor(editorRef.current);
        const editor = editorRef.current;
        if (editor && getRichTextPlainLength(editor.innerHTML) === 0) {
          editor.innerHTML = "";
          const range = window.document.createRange();
          range.selectNodeContents(editor);
          range.collapse(true);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        if (!insertHtmlAtCaret(html, editor)) {
          if (text) {
            insertTextAtCaret(text, editor);
          }
        }
        emitChange();
        refreshSelectionState(editorRef.current, setFormatState);
      },
      [emitChange]
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!CAN_USE_DOM || disabled) {
          return;
        }
        if (event.key !== "Tab") {
          return;
        }
        event.preventDefault();
        focusEditor(editorRef.current);
        if (event.shiftKey) {
          removeLeadingTabAtCaret();
        } else {
          insertTextAtCaret("\t", editorRef.current);
        }
        emitChange();
        refreshSelectionState(editorRef.current, setFormatState);
      },
      [disabled, emitChange]
    );

    const handleInlineFormatToggle = React.useCallback(
      (tagName: "B" | "I" | "U") => {
        if (!CAN_USE_DOM || disabled) {
          return;
        }
        focusEditor(editorRef.current);
        toggleInlineFormat(tagName, editorRef.current);
        emitChange();
        refreshSelectionState(editorRef.current, setFormatState);
      },
      [disabled, emitChange]
    );

    const handleListToggle = React.useCallback(
      (listTag: "UL" | "OL") => {
        if (!CAN_USE_DOM || disabled) {
          return;
        }
        focusEditor(editorRef.current);
        toggleList(listTag, editorRef.current);
        emitChange();
        refreshSelectionState(editorRef.current, setFormatState);
      },
      [disabled, emitChange]
    );

    const handleInsertParagraph = React.useCallback(() => {
      if (!CAN_USE_DOM || disabled) {
        return;
      }
      focusEditor(editorRef.current);
      insertParagraph(editorRef.current);
      emitChange();
      refreshSelectionState(editorRef.current, setFormatState);
    }, [disabled, emitChange]);

    const handleColorChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!CAN_USE_DOM || disabled) {
          return;
        }
        const nextColor = event.target.value;
        focusEditor(editorRef.current);
        applyTextColor(nextColor, editorRef.current);
        emitChange();
        refreshSelectionState(editorRef.current, setFormatState);
      },
      [disabled, emitChange]
    );

    const handleLinkClick = React.useCallback(() => {
      if (!CAN_USE_DOM || disabled) {
        return;
      }
      focusEditor(editorRef.current);
      const existingHref = findCurrentLinkHref(editorRef.current) || "https://";
      const url = window.prompt("Enter link URL", existingHref);
      if (url === null) {
        return;
      }
      const trimmed = url.trim();
      if (!trimmed) {
        removeLinkAtSelection(editorRef.current);
        emitChange();
        refreshSelectionState(editorRef.current, setFormatState);
        return;
      }
      const safe = ensureSafeUrl(trimmed);
      if (!safe) {
        window.alert("Please enter a valid URL starting with http://, https://, or mailto:");
        return;
      }
      createLinkAtSelection(safe, editorRef.current);
      emitChange();
      refreshSelectionState(editorRef.current, setFormatState);
    }, [disabled, emitChange]);

    React.useEffect(() => {
      if (!CAN_USE_DOM) {
        return;
      }
      const handler = () => {
        refreshSelectionState(editorRef.current, setFormatState);
      };
      window.document.addEventListener("selectionchange", handler);
      return () => {
        window.document.removeEventListener("selectionchange", handler);
      };
    }, []);

    const showPlaceholder = getRichTextPlainLength(lastValueRef.current) === 0 && !isFocused && !!placeholder;
    const borderColor = isFocused ? "#4096ff" : "#d9d9d9";

    return (
      <div
        className={className}
        id={id}
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: 6,
          transition: "border-color 0.2s",
          background: disabled ? "#f5f5f5" : "#fff",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <style>
          {`
            .rich-text-editor__content p,
            .rich-text-editor__content div {
              margin: 0 0 0.75em;
              min-height: 1.25em;
            }
            .rich-text-editor__content p:last-child,
            .rich-text-editor__content div:last-child {
              margin-bottom: 0;
            }
            .rich-text-editor__content ul,
            .rich-text-editor__content ol {
              margin: 0 0 0.75em;
              padding-left: 1.5em;
            }
          `}
        </style>
        <div style={toolbarStyle}>
          <button
            type="button"
            aria-label="Bold"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleInlineFormatToggle("B")}
            style={{
              ...buttonBaseStyle,
              fontWeight: 600,
              background: formatState.bold ? "#e6f4ff" : "transparent",
            }}
            disabled={disabled}
          >
            B
          </button>
          <button
            type="button"
            aria-label="Italic"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleInlineFormatToggle("I")}
            style={{
              ...buttonBaseStyle,
              fontStyle: "italic",
              background: formatState.italic ? "#e6f4ff" : "transparent",
            }}
            disabled={disabled}
          >
            I
          </button>
          <button
            type="button"
            aria-label="Underline"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleInlineFormatToggle("U")}
            style={{
              ...buttonBaseStyle,
              textDecoration: "underline",
              background: formatState.underline ? "#e6f4ff" : "transparent",
            }}
            disabled={disabled}
          >
            U
          </button>
          <label
            aria-label="Font colour"
            title="Font colour"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 4px",
              borderRadius: 4,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1,
                borderBottom: `3px solid ${formatState.color}`,
                paddingBottom: 1,
              }}
            >
              A
            </span>
            <input
              type="color"
              value={formatState.color}
              onMouseDown={(event) => event.preventDefault()}
              onChange={handleColorChange}
              disabled={disabled}
              aria-label="Choose font colour"
              style={colorInputStyle}
            />
          </label>
          <button
            type="button"
            aria-label="Bullet list"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleListToggle("UL")}
            style={{
              ...buttonBaseStyle,
              background: formatState.unordered ? "#e6f4ff" : "transparent",
            }}
            disabled={disabled}
          >
            ••
          </button>
          <button
            type="button"
            aria-label="Numbered list"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleListToggle("OL")}
            style={{
              ...buttonBaseStyle,
              background: formatState.ordered ? "#e6f4ff" : "transparent",
            }}
            disabled={disabled}
          >
            1.
          </button>
          <button
            type="button"
            aria-label="Insert link"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleLinkClick}
            style={{
              ...buttonBaseStyle,
              textDecoration: "underline",
              background: formatState.link ? "#e6f4ff" : "transparent",
            }}
            disabled={disabled}
          >
            🔗
          </button>
          <button
            type="button"
            aria-label="Insert paragraph"
            title="Insert paragraph / empty line"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleInsertParagraph}
            style={{
              ...buttonBaseStyle,
            }}
            disabled={disabled}
          >
            ¶
          </button>
        </div>
        <div
          style={{
            position: "relative",
            flex: 1,
          }}
        >
          <div
            ref={editorRef}
            className="rich-text-editor__content"
            contentEditable={!disabled}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={ariaLabel}
            tabIndex={0}
            style={{
              padding: "8px 12px",
              minHeight: 160,
              outline: "none",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight,
              overflowY: "auto",
              cursor: disabled ? "not-allowed" : "text",
              background: disabled ? "#f5f5f5" : "transparent",
              tabSize: 4,
            }}
            onInput={handleInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
          />
          {showPlaceholder && <div style={placeholderStyle}>{placeholder}</div>}
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;